import { PageShell } from "@superadmin/components/PageShell";
import { orpc } from "@superadmin/utils/orpc.tanstack.client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LoadingSpinner } from "@voicely/ui-mui/components/LoadingSpinner";
import { Chip } from "@voicely/ui-mui/data-display/Chip";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@voicely/ui-mui/data-display/Table";
import { Typography } from "@voicely/ui-mui/data-display/Typography";
import { Alert } from "@voicely/ui-mui/feedback/Alert";
import { Button } from "@voicely/ui-mui/form/Button";
import { TextField } from "@voicely/ui-mui/form/TextField";
import { Paper } from "@voicely/ui-mui/layout/Paper";
import { Stack } from "@voicely/ui-mui/layout/Stack";
import { useUrlState } from "@voicely/web-shared/use_url_state";
import {
	PLATFORM_PROMPT_MAX_CHARS,
	type PlatformPromptVersionView,
} from "@voicely/zod-schemas/platform_prompt.zod";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { z } from "zod";

/**
 * The platform system prompt: conduct rules every agent is given in front of
 * its own prompt. A save is a new version, in force for calls that start after
 * it; a call records the version it ran under.
 *
 * The version being looked at is in the URL (`?version=`), so a link to "the
 * prompt we ran on Tuesday" opens on that text. The draft in the editor is
 * form state and stays in memory, as every other editor's draft does.
 */
export const PlatformPromptPage = () => {
	const queryClient = useQueryClient();
	const stateQuery = useQuery(orpc.superAdmin.platformPrompt.queryOptions());
	const [viewing, setViewing] = useUrlState("version", z.string(), "");
	const [draft, setDraft] = useState<string | null>(null);

	const current = stateQuery.data?.current ?? null;

	// The editor starts on the text in force, once it has loaded, and is
	// not reset by a refetch — a background refresh must not eat an edit.
	useEffect(() => {
		if (draft === null && stateQuery.data) setDraft(stateQuery.data.current?.text ?? "");
	}, [draft, stateQuery.data]);

	const save = useMutation(
		orpc.platformPrompt.save.mutationOptions({
			onSuccess: async (state) => {
				toast.success(
					state.current?.text
						? "Saved. New calls get this prompt."
						: "Saved empty: the platform prompt is off.",
				);
				setDraft(state.current?.text ?? "");
				setViewing("");
				await queryClient.invalidateQueries({ queryKey: orpc.superAdmin.platformPrompt.key() });
			},
		}),
	);

	if (stateQuery.isPending) {
		return (
			<PageShell title="System prompt">
				<LoadingSpinner />
			</PageShell>
		);
	}

	if (stateQuery.error) {
		return (
			<PageShell title="System prompt">
				<Alert severity="error">{stateQuery.error.message}</Alert>
			</PageShell>
		);
	}

	const { versions, defaultText } = stateQuery.data;
	const text = draft ?? "";
	const unchanged = text.trim() === (current?.text ?? "");
	const viewed = versions.find((v) => v.id === viewing) ?? null;
	const error = save.error as { message?: string } | null;

	return (
		<PageShell
			title="System prompt"
			subtitle="Put in front of every agent's own prompt on every call. The agent's instructions win where they differ."
		>
			<Stack spacing={3}>
				<Paper variant="outlined" sx={{ p: 2 }}>
					<Stack spacing={2}>
						{error?.message && <Alert severity="error">{error.message}</Alert>}
						<Typography variant="body2" color="text.secondary">
							Every word here is sent on every turn of every call, so keep it to rules that hold for
							any agent. Script, facts and persona belong in the agent. Saving empty switches the
							platform prompt off.
						</Typography>
						<TextField
							multiline
							minRows={14}
							fullWidth
							value={text}
							onChange={(event) => setDraft(event.target.value)}
							slotProps={{ htmlInput: { maxLength: PLATFORM_PROMPT_MAX_CHARS } }}
							helperText={`${wordCount(text)} words · ${text.length}/${PLATFORM_PROMPT_MAX_CHARS} characters`}
							sx={{ "& textarea": { fontFamily: "monospace", fontSize: 13 } }}
						/>
						<Stack direction="row" spacing={1}>
							<Button
								variant="contained"
								disabled={save.isPending || unchanged}
								onClick={() => save.mutate({ text })}
							>
								Save as new version
							</Button>
							<Button
								disabled={save.isPending || unchanged}
								onClick={() => setDraft(current?.text ?? "")}
							>
								Discard changes
							</Button>
							<Button
								disabled={save.isPending || text === defaultText}
								onClick={() => setDraft(defaultText)}
							>
								Load the default
							</Button>
						</Stack>
					</Stack>
				</Paper>

				<Stack spacing={1}>
					<Typography variant="h6">History</Typography>
					<TableContainer component={Paper} variant="outlined">
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Saved</TableCell>
									<TableCell>By</TableCell>
									<TableCell align="right">Words</TableCell>
									<TableCell />
								</TableRow>
							</TableHead>
							<TableBody>
								{versions.map((version) => (
									<TableRow key={version.id} selected={version.id === viewing}>
										<TableCell>
											{new Date(version.createdAt).toLocaleString()}{" "}
											{version.id === current?.id && (
												<Chip size="small" color="success" label="In force" />
											)}
										</TableCell>
										<TableCell>
											{version.createdByEmail ??
												(version.createdByUserId ? "deleted user" : "seed")}
										</TableCell>
										<TableCell align="right">
											{version.text === "" ? "off" : wordCount(version.text)}
										</TableCell>
										<TableCell align="right">
											<Button
												size="small"
												onClick={() => setViewing(version.id === viewing ? "" : version.id)}
											>
												{version.id === viewing ? "Hide" : "View"}
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Stack>

				{viewed && <ViewedVersion version={viewed} onLoad={() => setDraft(viewed.text)} />}
			</Stack>
		</PageShell>
	);
};

const ViewedVersion = ({
	version,
	onLoad,
}: {
	version: PlatformPromptVersionView;
	onLoad: () => void;
}) => (
	<Paper variant="outlined" sx={{ p: 2 }}>
		<Stack spacing={1}>
			<Typography variant="subtitle2">
				Version saved {new Date(version.createdAt).toLocaleString()} ·{" "}
				<Typography component="span" variant="body2" sx={{ fontFamily: "monospace" }}>
					{version.id}
				</Typography>
			</Typography>
			<Typography
				component="pre"
				variant="body2"
				sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: 13, m: 0 }}
			>
				{version.text === "" ? "(empty: the platform prompt was off)" : version.text}
			</Typography>
			<Stack direction="row">
				{/* Into the editor, not straight to a save: restoring is a new
				    version, and it should be a deliberate one. */}
				<Button size="small" onClick={onLoad}>
					Load into editor
				</Button>
			</Stack>
		</Stack>
	</Paper>
);

function wordCount(text: string): number {
	const trimmed = text.trim();

	return trimmed === "" ? 0 : trimmed.split(/\s+/).length;
}
